// Color options shared by every surface that picks or prints roof colors
// (Oct 22, 2026, per Greg): the homeowner signing page, the /colors
// confirmation page, the staff deal page, and both PDFs.
//
// Drip edge comes in exactly three colors: White, Black, Brown. Shingle
// lists stay where they are (the signing page's dropdown and the /colors
// page's swatches serve different moments), so only drip edge lives here.

export const DRIP_EDGE_COLORS = [
  { name: "White", hex: "#f7f7f5" },
  { name: "Black", hex: "#1c1c1c" },
  { name: "Brown", hex: "#5c4033" },
] as const;

export type DripEdgeColor = (typeof DRIP_EDGE_COLORS)[number]["name"];

export const DRIP_EDGE_COLOR_NAMES: readonly DripEdgeColor[] = DRIP_EDGE_COLORS.map((c) => c.name);

/** "White / Black / Brown" — for footnotes and hints. */
export const DRIP_EDGE_COLOR_LIST = DRIP_EDGE_COLOR_NAMES.join(" / ");

/**
 * Map a stored drip edge value onto one of the three options, or null.
 * Older rows carry hand-typed values ("Black ", "black", "Color: Black",
 * "Black drip"); those read as Black. A value that is not one of the three
 * ("Musket Brown", "Weathered Wood") returns null so the caller can show it
 * as-is instead of silently changing the homeowner's record.
 */
export function matchDripEdgeColor(raw: string | null | undefined): DripEdgeColor | null {
  if (!raw) return null;
  const cleaned = raw
    .toLowerCase()
    .replace(/^\s*colou?rs?\s*:\s*/, "")
    .replace(/\s*\bdrip(\s+edge)?\b\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
  const hit = DRIP_EDGE_COLORS.find((c) => c.name.toLowerCase() === cleaned);
  return hit ? hit.name : null;
}

/** True when the value is one of the three options as stored (exact spelling). */
export function isStandardDripEdgeColor(raw: string | null | undefined): raw is DripEdgeColor {
  return !!raw && (DRIP_EDGE_COLOR_NAMES as readonly string[]).includes(raw);
}

// ---------------------------------------------------------------------------
// What the agreement says about colors
// ---------------------------------------------------------------------------

export interface ColorSelectionSummary {
  shingle: string;
  drip: string;
  shingleChosen: boolean;
  dripChosen: boolean;
  /** Both colors are on file: the agreement itself is the color authorization. */
  bothChosen: boolean;
}

export const COLOR_TO_BE_CONFIRMED = "To be confirmed";

/** A blank, whitespace-only or "not_yet" value counts as not chosen. */
function chosen(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  if (!t || t.toLowerCase() === "not_yet") return null;
  return t;
}

export function summarizeColorSelections(
  shingle: string | null | undefined,
  drip: string | null | undefined,
): ColorSelectionSummary {
  const s = chosen(shingle);
  const d = chosen(drip);
  return {
    shingle: s ?? COLOR_TO_BE_CONFIRMED,
    drip: d ?? COLOR_TO_BE_CONFIRMED,
    shingleChosen: !!s,
    dripChosen: !!d,
    bothChosen: !!s && !!d,
  };
}

// ---------------------------------------------------------------------------
// Homeowner portal: when to show the "Choose / Confirm your colors" banner
// ---------------------------------------------------------------------------

export interface ColorPromptInput {
  install_agreement_signed: boolean | null;
  color_addendum_signed: boolean | null;
  color_addendum_required?: boolean | null;
  material_order_date: string | null;
  shingle_color: string | null;
  drip_edge_color: string | null;
}

/**
 * "choose" when the homeowner still has to pick a color, "confirm" when both
 * colors are on the deal (typed in by staff after a deferred signing) and only
 * the signature is missing, null when nothing is owed: no signed agreement
 * yet, colors chosen at signing (the agreement is the authorization), the
 * addendum already signed, or materials already ordered (the /colors page is
 * read-only then; changes go through a change order).
 */
export function homeownerColorPrompt(d: ColorPromptInput): "choose" | "confirm" | null {
  if (d.install_agreement_signed !== true) return null;
  if (d.color_addendum_signed === true) return null;
  if (d.material_order_date) return null;
  const both = !!(d.shingle_color ?? "").trim() && !!(d.drip_edge_color ?? "").trim();
  if (both && d.color_addendum_required !== true) return null;
  return both ? "confirm" : "choose";
}

/**
 * The line printed under the colors on the install agreement when a color is
 * still open, or null when both are chosen. (When both are chosen the
 * agreement's own terms make the signature the color authorization, so page 1
 * stays one line.)
 */
export function colorDeferredNote(summary: ColorSelectionSummary): string | null {
  if (summary.bothChosen) return null;
  if (!summary.shingleChosen && !summary.dripChosen) {
    return "Colors to be confirmed on a separate Color Confirmation Addendum, signed by Homeowner before materials are ordered.";
  }
  const missing = summary.shingleChosen ? "drip edge" : "shingle";
  return `The ${missing} color is confirmed on a separate Color Confirmation Addendum, signed by Homeowner before materials are ordered.`;
}
